// src/components/SendingWeeks.jsx
// Sending activity (3.80.1, owner, Nov 4): the faxes of each week, portal and
// email stacked, and the words for its tiles. Point at a colour or at its name
// in the key: it stands out, the other fades, and the week's numbers show.
import { useState } from 'react';

const SERIES = [
  { id: 'portal', name: 'Portal', color: 'var(--c-00897b)' },
  { id: 'email', name: 'Email', color: 'var(--c-fb8c00)' },
];
const CSS = `
.sa-col { animation: saGrow .55s cubic-bezier(.2,.8,.3,1) both; transform-origin: bottom; }
.sa-seg { transition: transform .18s ease, opacity .18s ease, box-shadow .18s ease; transform-origin: bottom; outline: none; }
.sa-seg.sa-on { transform: scaleX(1.14); box-shadow: 0 2px 10px rgb(var(--c-000000-rgb) / 0.28); }
.sa-seg.sa-off { opacity: .28; }
.sa-key { transition: opacity .18s ease; }
.sa-key.sa-off { opacity: .45; }
.sa-tip { animation: saTip .14s ease both; }
@keyframes saGrow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes saTip { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .sa-col, .sa-tip { animation: none; } .sa-seg, .sa-key { transition: none; } }
`;
const dayText = iso => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const share = (n, all) => (all ? `${Math.round((n / all) * 100)}%` : '0%');

export default function SendingWeeks({ weeks = [], height = 130 }) {
  const [on, setOn] = useState(null);   // { week?, series }
  const top = Math.max(1, ...weeks.map(w => w.portal + w.email));
  const whole = id => weeks.reduce((n, w) => n + w[id], 0);
  const last = weeks[weeks.length - 1]?.week;
  return (
    <div>
      <style>{CSS}</style>
      <div style={S.key}>
        {SERIES.map(s => (
          <span key={s.id} className={`sa-key${on && on.series !== s.id ? ' sa-off' : ''}`} style={S.keyItem} tabIndex={0}
            onMouseEnter={() => setOn({ series: s.id })} onMouseLeave={() => setOn(null)} onFocus={() => setOn({ series: s.id })} onBlur={() => setOn(null)}>
            <span style={{ ...S.swatch, background: s.color }} />{s.name} <strong>{whole(s.id).toLocaleString()}</strong>
          </span>
        ))}
      </div>
      <div style={{ ...S.plot, height: height + 40 }}>
        {weeks.map((w, i) => {
          const total = w.portal + w.email;
          const shown = on?.week === w.week;
          return (
            <div key={w.week} style={S.week}>
              {shown && (
                <div className="sa-tip" role="tooltip" style={{ ...S.tip, ...(i > weeks.length / 2 ? { right: 0 } : { left: 0 }) }}>
                  <div style={S.tipHead}>Week of {dayText(w.week)}{w.week === last ? ' (so far)' : ''}</div>
                  {SERIES.map(s => (
                    <div key={s.id} style={{ ...S.tipRow, ...(on.series === s.id ? { fontWeight: 700 } : null) }}>
                      <span style={{ ...S.tipLine, background: s.color }} /><strong>{w[s.id].toLocaleString()}</strong> {s.name} ({share(w[s.id], total)})
                    </div>
                  ))}
                </div>
              )}
              <div style={S.total}>{total.toLocaleString()}</div>
              <div className="sa-col" style={{ ...S.col, height: Math.max(4, (total / top) * height), animationDelay: `${i * 50}ms` }}>
                {[...SERIES].reverse().map(s => {
                  const n = w[s.id];
                  if (!n) return null;
                  const px = (n / top) * height;
                  const state = !on ? '' : on.series === s.id && (!on.week || on.week === w.week) ? ' sa-on' : ' sa-off';
                  return (
                    <div key={s.id} className={`sa-seg${state}`} tabIndex={0} aria-label={`Week of ${dayText(w.week)}: ${n} ${s.name} faxes`} style={{ ...S.seg, flex: `${n} 0 0`, background: s.color }}
                      onMouseEnter={() => setOn({ week: w.week, series: s.id })} onMouseLeave={() => setOn(null)}
                      onFocus={() => setOn({ week: w.week, series: s.id })} onBlur={() => setOn(null)}>
                      {px >= 18 && <span style={S.segNum}>{n}</span>}
                    </div>
                  );
                })}
              </div>
              <div style={S.day}>{dayText(w.week)}{w.week === last ? ' so far' : ''}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// What each tile counts, in plain words (Overview strip and Logs)
export const sendingTiles = t => [
  ['People who sent', `${(t.sent || 0).toLocaleString()} of ${(t.people || 0).toLocaleString()}`, 'Sent at least one fax in this period'],
  ['Portal faxes', t.viaPortal, 'Sent from the portal'],
  ['Email faxes', t.viaEmail, 'Sent by email to fax'],
  ['Portal only', t.portalOnly, 'People who sent from the portal, never by email'],
  ['Email only', t.emailOnly, 'People who sent by email, never from the portal'],
  ['Both', t.both, 'People who sent both ways'],
  ['Stopped sending', t.stopped, 'Sent before, but nothing in the last 14 days'],
  ['Not sending', t.notSending, `Sent nothing. ${(t.inboxOnly || 0).toLocaleString()} of them opened Inbox faxes`],
];

const S = {
  key: { display: 'flex', gap: 14, justifyContent: 'flex-end', fontSize: 12, color: 'var(--t-546e7a)', marginBottom: 2 },
  keyItem: { display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'default', borderRadius: 4, padding: '1px 3px' },
  swatch: { width: 12, height: 12, borderRadius: 3 },
  plot: { display: 'flex', gap: 'clamp(8px, 2.5vw, 26px)', alignItems: 'flex-end', paddingTop: 4 },
  week: { position: 'relative', flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 3 },
  total: { fontSize: 12, fontWeight: 700, color: 'var(--t-37474f)' },
  col: { width: '100%', maxWidth: 58, display: 'flex', flexDirection: 'column', gap: 2 },
  seg: { minHeight: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: 4 },
  segNum: { fontSize: 11, fontWeight: 700, color: 'var(--c-ffffff)', textShadow: '0 1px 2px rgb(var(--c-000000-rgb) / 0.45)', pointerEvents: 'none' },
  day: { fontSize: 11, color: 'var(--t-78909c)', whiteSpace: 'nowrap' },
  tip: { position: 'absolute', bottom: '100%', marginBottom: -6, zIndex: 5, minWidth: 158, background: 'var(--c-263238)', color: 'var(--c-ffffff)', padding: '8px 10px', borderRadius: 8, boxShadow: '0 4px 14px rgb(var(--c-000000-rgb) / 0.3)', fontSize: 12, pointerEvents: 'none' },
  tipHead: { fontSize: 11, opacity: 0.8, marginBottom: 4, whiteSpace: 'nowrap' },
  tipRow: { display: 'flex', alignItems: 'center', gap: 6, padding: '1px 0', whiteSpace: 'nowrap' },
  tipLine: { width: 12, height: 3, borderRadius: 2 },
};
